/**
 * Questionnaire Component (Main Container)
 *
 * Main component that orchestrates the entire questionnaire experience:
 * - Loads questionnaire structure
 * - Manages navigation between categories
 * - Handles response collection
 * - Shows progress
 * - Displays results upon completion
 */

import React, { useEffect } from "react";

import { CategoryView } from "./components/CategoryView";
import { ProgressBar } from "./components/ProgressBar";
import { ResultsSummary } from "./components/ResultsSummary";
import { useQuestionnaireState } from "./hooks/useQuestionnaireState";
import {
  getNextCategory,
  getPreviousCategory,
  isFirstCategory,
  isLastCategory,
  calculateCategoryProgress,
} from "./utils/navigation.utils";

interface QuestionnaireProps {
  userId?: string;
}

export const Questionnaire: React.FC<QuestionnaireProps> = ({ userId }) => {
  const {
    questionnaire,
    responses,
    loading,
    saving,
    submitting,
    error,
    setResponse,
    saveProgress,
    submitAssessment,
    resetAssessment,
    currentCategoryId,
    setCurrentCategoryId,
    isComplete,
  } = useQuestionnaireState({ userId });

  const [showResults, setShowResults] = React.useState(false);
  const [lastSaved, setLastSaved] = React.useState<Date | undefined>(undefined);

  // Update last saved time when saving completes
  useEffect(() => {
    if (!saving) {
      setLastSaved(new Date());
    }
  }, [saving]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading questionnaire...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !questionnaire) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-bold text-gray-900">
              Error Loading Questionnaire
            </h2>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!questionnaire || !currentCategoryId) {
    return null;
  }

  // Show results if assessment is complete and submitted
  if (showResults && isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <ResultsSummary
          layers={questionnaire.layers}
          categories={questionnaire.categories}
          questions={questionnaire.questions}
          responses={Array.from(responses.values())}
          onStartOver={() => {
            resetAssessment();
            setShowResults(false);
          }}
          onDownloadReport={() => {
            // TODO: Implement report download
            console.log("Download report");
          }}
        />
      </div>
    );
  }

  const currentCategory = questionnaire.categories.find(
    (c) => c.id === currentCategoryId,
  );

  if (!currentCategory) {
    return null;
  }

  const categoryProgress = calculateCategoryProgress(
    currentCategoryId,
    questionnaire.categories,
  );

  const handleNext = async () => {
    // Save progress before navigating
    await saveProgress();

    const next = getNextCategory(currentCategoryId, questionnaire.categories);
    if (next) {
      setCurrentCategoryId(next.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (isComplete) {
      // Last category and all questions answered - show results
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    const prev = getPreviousCategory(
      currentCategoryId,
      questionnaire.categories,
    );
    if (prev) {
      setCurrentCategoryId(prev.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSubmit = async () => {
    try {
      await submitAssessment();
      setShowResults(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error submitting assessment:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <ProgressBar
        answeredQuestions={responses.size}
        totalQuestions={questionnaire.questions.length}
        saving={saving}
        lastSaved={lastSaved}
      />

      {/* Main Content */}
      <div className="py-8">
        <CategoryView
          category={currentCategory}
          questions={questionnaire.questions}
          responses={responses}
          onResponseChange={setResponse}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isFirst={isFirstCategory(currentCategoryId, questionnaire.categories)}
          isLast={isLastCategory(currentCategoryId, questionnaire.categories)}
          categoryNumber={categoryProgress.currentIndex}
          totalCategories={categoryProgress.totalCategories}
        />
      </div>

      {/* Error Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 max-w-md bg-red-50 border border-red-200 rounded-lg shadow-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-500 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 mb-1">Error</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submitting Overlay */}
      {submitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-sm text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Submitting Assessment
            </h3>
            <p className="text-gray-600">Calculating your readiness score...</p>
          </div>
        </div>
      )}
    </div>
  );
};
